import {
  formatOverviewCardDisplayValue,
  coerceNumber,
  readMoney,
} from "@/shared/lib/overview/overview-formatting"
import { chartHasRenderableData as overviewChartHasRenderableData } from "@/shared/lib/overview/overview-chart-utils"
import { FinanceOverviewSummaryKey } from "@ibada/shared-types/finance"
import type { OverviewResponse } from "@ibada/shared-types/overview"
import { formatDate } from "@/features/finance/lib/finance-utils"
import { createCurrencyFormatter, type DisplayAmount } from "@/shared/lib/formatting/currency"
import {
  FinanceOverviewCardId,
  FinanceOverviewChartId,
  FinanceOverviewSeriesKey,
  OverviewTableColumnType,
  type NormalizedFinanceOverviewResponse,
  type OverviewCard,
  type OverviewChart,
} from "@/features/finance/model/finance-normalized-overview"
import { TransactionDirection } from "@/features/finance/model/finance-types"

export const FINANCE_CARD_DISPLAY_ORDER = [
  FinanceOverviewCardId.TotalIncome,
  FinanceOverviewCardId.TotalExpenses,
  FinanceOverviewCardId.NetBalance,
  FinanceOverviewCardId.TransactionCount,
  FinanceOverviewCardId.ActiveActivitiesCount,
] as const

export function findOverviewCard(cards: OverviewCard[], id: FinanceOverviewCardId): OverviewCard | undefined {
  return cards.find((card) => card.id === id)
}

export function pickCardsForDashboard(cards: OverviewCard[]): OverviewCard[] {
  return FINANCE_CARD_DISPLAY_ORDER.map((id) => findOverviewCard(cards, id)).filter(
    (card): card is OverviewCard => Boolean(card),
  )
}

export { coerceNumber, readMoney, formatOverviewCardDisplayValue }

export function chartHasRenderableData(chart: OverviewChart): boolean {
  return overviewChartHasRenderableData(chart)
}

export function buildIncomeExpenseTrendRows(chart: OverviewChart): IncomeExpenseTrendRow[] {
  if (!chart.labels.length) return []

  const byKey = (key: string) => chart.series.find((s) => s.key === key)?.data ?? []

  const income = byKey(FinanceOverviewSeriesKey.Income)
  const expense = byKey(FinanceOverviewSeriesKey.Expense)

  return chart.labels.map((label, index) => {
    const row: IncomeExpenseTrendRow = { period: label }
    if (index < income.length) row.income = income[index] ?? 0
    if (index < expense.length) row.expense = expense[index] ?? 0
    return row
  })
}

/** Row objects keyed by chart period label; series aligned to chart.labels order */
export type IncomeExpenseTrendRow = Record<string, string | number>

export type HorizontalBarRow = {
  label: string
  net: number
  /**
   * This bar's share of the chart's total, in BASIS POINTS (0–10000), as allocated by the backend. Render it as
   * `shareBp / 100` — never re-derive a percentage here. Per-slice `Math.round()` in the browser produced shares
   * that did not sum to 100%, and the total it divided by was one the server already knew (IBA-117).
   */
  shareBp: number
}

export function buildHorizontalBarRows(chart: OverviewChart): HorizontalBarRow[] {
  const netSeries = chart.series.find((s) => s.key === FinanceOverviewSeriesKey.Net)
  if (!netSeries) return []

  return chart.labels
    .map((label, index) => ({
      label,
      net: netSeries.data[index] ?? 0,
      shareBp: chart.shares?.[index] ?? 0,
    }))
    .filter((row) => Number.isFinite(row.net) && row.net !== 0)
}

/** A backend-allocated basis-point share, as a percentage number for display. The only conversion allowed. */
export function basisPointsToPercent(shareBp: number): number {
  return shareBp / 100
}

export function formatTableCellValue(
  raw: unknown,
  columnType: OverviewTableColumnType,
  currencyCode: string,
  locale: string,
): string {
  if (raw === null || raw === undefined) return "—"

  if (columnType === OverviewTableColumnType.Badge) {
    return String(raw)
  }

  if (columnType === OverviewTableColumnType.Date) {
    return formatDate(String(raw)) ?? String(raw)
  }

  if (columnType === OverviewTableColumnType.Money) {
    // The cell is a canonical decimal string (IBA-116). It goes to `Intl` untouched — `Number(raw)` here was a
    // lossy step taken on the way to a formatter that parses the string exactly.
    const amount = readMoney(raw)
    if (amount === null) return String(raw)
    return createCurrencyFormatter(currencyCode, locale).format(amount)
  }

  if (columnType === OverviewTableColumnType.Number) {
    const n = coerceNumber(raw)
    if (n === null) return String(raw)
    return n.toLocaleString(locale, { maximumFractionDigits: 2 })
  }

  if (columnType === OverviewTableColumnType.Text) {
    return String(raw)
  }

  return String(raw)
}

/**
 * The scope's cash position, as the backend computed it (IBA-117).
 *
 * No caller may re-derive this from a list of accounts: the dashboard used to sum a *page* of accounts in the
 * browser, so a church with more accounts than that page silently displayed a total that was too small. The
 * total is the server's; `hideZero` only picks WHICH COUNT to show beside it (a zero-balance account cannot
 * change a sum).
 */
export function readFinanceCashPosition(
  response: NormalizedFinanceOverviewResponse | OverviewResponse | undefined,
): FinanceCashPosition | undefined {
  const summary = response?.summary
  if (!summary) return undefined

  return {
    // `totalCash` is money — it stays the exact decimal string the server sent. The two COUNTS beside it are
    // genuine numbers and are read as such; that distinction is the whole of this function.
    totalCash: readMoney(summary[FinanceOverviewSummaryKey.TotalCash]) ?? "0",
    accountCount: coerceNumber(summary[FinanceOverviewSummaryKey.AccountCount]) ?? 0,
    nonZeroAccountCount: coerceNumber(summary[FinanceOverviewSummaryKey.NonZeroAccountCount]) ?? 0,
  }
}

export interface FinanceCashPosition {
  /** Money: the canonical decimal string the backend summed over EVERY account in scope. Never re-derived. */
  totalCash: DisplayAmount
  accountCount: number
  nonZeroAccountCount: number
}

export function findChartById(
  response: NormalizedFinanceOverviewResponse | OverviewResponse | undefined,
  id: FinanceOverviewChartId,
): OverviewChart | undefined {
  return response?.charts.find((chart) => chart.id === id)
}

export function findTableById(response: NormalizedFinanceOverviewResponse | OverviewResponse | undefined, id: string) {
  return response?.tables.find((table) => table.id === id)
}

export function tableHasRenderableData(
  response: NormalizedFinanceOverviewResponse | OverviewResponse | undefined,
  id: string,
): boolean {
  const table = findTableById(response, id)
  return Boolean(table && table.columns.length > 0 && table.rows.length > 0)
}

export function normalizeTransactionDirectionLabel(
  raw: unknown,
  translateDirection: (key: string) => string,
): string {
  const value = typeof raw === "string" ? raw : ""
  const upper = value.toUpperCase()
  if (upper === "INCOME" || value === TransactionDirection.Income) {
    return translateDirection("INCOME")
  }
  if (upper === "EXPENSE" || value === TransactionDirection.Expense) {
    return translateDirection("EXPENSE")
  }
  return value || "—"
}
